import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import SimplicitySection from '../components/SimplicitySection';
import './HowItWorks.css';

function HowItWorks() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // FAQs from translation
  const faqs = [
    {
      question: t('howItWorks.faq.q1.question'),
      answer: t('howItWorks.faq.q1.answer')
    },
    {
      question: t('howItWorks.faq.q2.question'),
      answer: t('howItWorks.faq.q2.answer')
    },
    {
      question: t('howItWorks.faq.q3.question'),
      answer: t('howItWorks.faq.q3.answer')
    },
    {
      question: t('howItWorks.faq.q4.question'),
      answer: t('howItWorks.faq.q4.answer')
    },
    {
      question: t('howItWorks.faq.q5.question'),
      answer: t('howItWorks.faq.q5.answer')
    },
    {
      question: t('howItWorks.faq.q6.question'),
      answer: t('howItWorks.faq.q6.answer')
    },
    {
      question: t('howItWorks.faq.q7.question'),
      answer: t('howItWorks.faq.q7.answer')
    },
    {
      question: t('howItWorks.faq.q8.question'),
      answer: t('howItWorks.faq.q8.answer')
    },
    {
      question: t('howItWorks.faq.q9.question'),
      answer: t('howItWorks.faq.q9.answer')
    },
    {
      question: t('howItWorks.faq.q10.question'),
      answer: t('howItWorks.faq.q10.answer')
    },
    {
      question: t('howItWorks.faq.q11.question'),
      answer: t('howItWorks.faq.q11.answer')
    },
    {
      question: t('howItWorks.faq.q12.question'),
      answer: t('howItWorks.faq.q12.answer')
    }
  ];

  // Use cases from translation
  const useCases = [
    {
      emoji: "👥",
      title: t('howItWorks.useCases.fridayHangout.title'),
      description: t('howItWorks.useCases.fridayHangout.description')
    },
    {
      emoji: "⚽",
      title: t('howItWorks.useCases.football.title'),
      description: t('howItWorks.useCases.football.description')
    },
    {
      emoji: "🎉",
      title: t('howItWorks.useCases.monthlyMeeting.title'),
      description: t('howItWorks.useCases.monthlyMeeting.description')
    },
    {
      emoji: "📚",
      title: t('howItWorks.useCases.studyGroup.title'),
      description: t('howItWorks.useCases.studyGroup.description')
    },
    {
      emoji: "🏃",
      title: t('howItWorks.useCases.runningGroup.title'),
      description: t('howItWorks.useCases.runningGroup.description')
    },
    {
      emoji: "🎲",
      title: t('howItWorks.useCases.gameNight.title'),
      description: t('howItWorks.useCases.gameNight.description')
    }
  ];

  return (
    <>
      <Navbar />
      <div className="how-it-works-container">
        {/* Hero Section */}
        <section className="hero-section">
          <h1>{t('howItWorks.hero.title')}</h1>
          <p className="hero-subtitle">
            {t('howItWorks.hero.subtitle')}
          </p>
        </section>

        {/* How It Works Steps */}
        <section className="steps-section">
          <h2>{t('howItWorks.steps.title')}</h2>
          
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">🎯</div>
              <h3>{t('howItWorks.steps.step1.title')}</h3>
              <p>{t('howItWorks.steps.step1.description')}</p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">👥</div>
              <h3>{t('howItWorks.steps.step2.title')}</h3>
              <p>{t('howItWorks.steps.step2.description')}</p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">✉️</div>
              <h3>{t('howItWorks.steps.step3.title')}</h3>
              <p>{t('howItWorks.steps.step3.description')}</p>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon">🎊</div>
              <h3>{t('howItWorks.steps.step4.title')}</h3>
              <p>{t('howItWorks.steps.step4.description')}</p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="features-section">
          <h2>{t('howItWorks.features.title')}</h2>
          
          <div className="features-grid">
            <div className="feature-item">
              <span className="feature-icon">🎯</span>
              <div>
                <h4>{t('howItWorks.features.threshold.title')}</h4>
                <p>{t('howItWorks.features.threshold.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">⚡</span>
              <div>
                <h4>{t('howItWorks.features.automatic.title')}</h4>
                <p>{t('howItWorks.features.automatic.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">🔗</span>
              <div>
                <h4>{t('howItWorks.features.selfRegistration.title')}</h4>
                <p>{t('howItWorks.features.selfRegistration.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">📋</span>
              <div>
                <h4>{t('howItWorks.features.copyMembers.title')}</h4>
                <p>{t('howItWorks.features.copyMembers.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">💚</span>
              <div>
                <h4>{t('howItWorks.features.noAccounts.title')}</h4>
                <p>{t('howItWorks.features.noAccounts.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">🔄</span>
              <div>
                <h4>{t('howItWorks.features.meetingTypes.title')}</h4>
                <p>{t('howItWorks.features.meetingTypes.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Simplicity Section - "Så enkelt är det" */}
        <SimplicitySection />

        {/* Use Cases */}
        <section className="use-cases-section">
          <h2>{t('howItWorks.useCases.title')}</h2>
          
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-emoji">{useCase.emoji}</div>
                <h4>{useCase.title}</h4>
                <p>{useCase.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>{t('howItWorks.faq.title')}</h2>
          
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className={`faq-question ${openFaq === index ? 'active' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <h2>{t('howItWorks.cta.title')}</h2>
          <p>{t('howItWorks.cta.subtitle')}</p>
          <div className="cta-buttons">
            <Link to="/signup" className="btn-primary">{t('howItWorks.cta.createAccount')}</Link>
            <Link to="/priser" className="btn-secondary">{t('howItWorks.cta.seePricing')}</Link>
          </div>
        </section>

        {/* Footer Links */}
        <section className="footer-links">
          <Link to="/terms">{t('howItWorks.footer.terms')}</Link>
          <span>•</span>
          <Link to="/privacy">{t('howItWorks.footer.privacy')}</Link>
        </section>
      </div>
    </>
  );
}

export default HowItWorks;